import { isAxiosError } from 'axios';
import { addDays, format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useCreateScheduledDrop } from '~entities/scheduled-drops/api/create-scheduled-drop';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Calendar } from '~shared/shadcn/ui/calendar';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import { Textarea } from '~shared/shadcn/ui/textarea';

interface ScheduleDropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollmentId: string;
  studentId: string;
  classStartDate?: string;
  classEndDate?: string;
  onSuccess?: () => void;
}

export function ScheduleDropDialog({
  open,
  onOpenChange,
  enrollmentId,
  studentId,
  classStartDate,
  classEndDate,
  onSuccess,
}: ScheduleDropDialogProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [reason, setReason] = useState('');
  const [shouldRefund, setShouldRefund] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const { mutateAsync: createScheduledDrop, isPending } =
    useCreateScheduledDrop();

  // Minimum date is the greater of: 30 days from now OR class start date
  const thirtyDaysFromNow = addDays(new Date(), 30);
  const classStartDateParsed = classStartDate
    ? new Date(`${classStartDate.split('T')[0]}T12:00:00`)
    : undefined;

  // Use the later of the two dates as the minimum
  const minDate =
    classStartDateParsed && classStartDateParsed > thirtyDaysFromNow
      ? classStartDateParsed
      : thirtyDaysFromNow;

  const maxDate = classEndDate
    ? new Date(`${classEndDate.split('T')[0]}T12:00:00`)
    : undefined;

  const handleSubmit = async () => {
    if (!selectedDate || !reason.trim()) return;

    try {
      await createScheduledDrop({
        studentId,
        enrollmentId,
        scheduledDropDate: format(selectedDate, 'yyyy-MM-dd'),
        dropReason: reason.trim(),
        shouldRefund,
      });
      toast.success('Drop scheduled successfully');
      onSuccess?.();
      handleClose();
    } catch (error: unknown) {
      const responseData = isAxiosError<{
        error?: { message?: string };
        message?: string;
      }>(error)
        ? error.response?.data
        : undefined;
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        'Failed to schedule drop';
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setSelectedDate(undefined);
    setReason('');
    setShouldRefund(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Schedule Drop</DialogTitle>
          <DialogDescription>
            Schedule a future date for the student to be dropped from this
            class. The drop date must be at least 30 days from today and within
            the class date range.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label>Drop Date</Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !selectedDate && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className='mr-2 size-4' />
                  {selectedDate
                    ? format(selectedDate, 'MMMM d, yyyy')
                    : 'Select a drop date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent align='start' className='w-auto p-0'>
                <Calendar
                  mode='single'
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setDatePickerOpen(false);
                  }}
                  disabled={[
                    { before: addDays(minDate, 1) },
                    ...(maxDate ? [{ after: maxDate }] : []),
                  ]}
                  defaultMonth={minDate}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className='space-y-2'>
            <Label>Reason</Label>
            <Textarea
              placeholder='Please provide a reason for scheduling this drop'
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              rows={3}
            />
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox
              id='schedule-drop-refund'
              checked={shouldRefund}
              onCheckedChange={(checked) => {
                setShouldRefund(checked === true);
              }}
            />
            <Label htmlFor='schedule-drop-refund'>
              Refund the prorated remaining tuition when the drop runs
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            disabled={!selectedDate || !reason.trim() || isPending}
            onClick={() => void handleSubmit()}
          >
            {isPending && <Loader2 className='mr-2 size-4 animate-spin' />}
            Schedule Drop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
